import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import * as Styled from './styled'
import { useCanvasScale } from './useCanvasScale'
import { usePlayheadOverlappingItems } from 'hooks/timeline'
import { convertMediaItemsToTimelineItems } from 'utils/timeline'

import { useTimelineContext } from 'context/TimelineContext'

import MediaControls from '../MediaControls'
import { projectDataSelectors } from 'store/features/projectData/selectors'

import { editorSelectors } from 'store/features/editor/selectors'
import { isActiveMediaItemPendingSelector } from 'store/selectors'
import { CANVAS_ID } from 'consts'
import { useWebWoodContext } from 'context/WebWoodContext'
import {
  EVENT_TYPES,
  useSubscribeToEvent
} from 'services/event-bus/useEventBus'
import CanvasMediaItem from './CanvasMediaItem'
import { shouldRenderCanvasMediaItem } from './helpers'

interface IProps {
  canvasWrapperEl: HTMLDivElement | null
}

const FilledCanvas: React.FC<IProps> = ({ canvasWrapperEl }) => {
  const activeTimelineItem = useSelector(editorSelectors.activeTimelineItem)
  const mediaItems = useSelector(projectDataSelectors.mediaItems)
  const { engine } = useWebWoodContext()
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const [canvasReady, setCanvasReady] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const existingCanvasElement = useMemo(() => {
    return engine?.canvas || null
  }, [engine])
  const { width: canvasWidth, height: canvasHeight } = useSelector(
    projectDataSelectors.defaultDimensions
  )
  const isAnyBuffering = useSelector(editorSelectors.isAnyBuffering)
  const timelineItems = useMemo(
    () => convertMediaItemsToTimelineItems(mediaItems),
    [mediaItems]
  )

  const [, setGenerationProgressVersion] = useState(0)

  const bumpGenerationProgressVersion = useCallback(() => {
    setGenerationProgressVersion(v => v + 1)
  }, [])

  useSubscribeToEvent(
    EVENT_TYPES.GENERATION_PROGRESS_UPDATE,
    bumpGenerationProgressVersion
  )

  const canvasOverlayMediaItems = mediaItems.filter(shouldRenderCanvasMediaItem)

  const { isPlaying } = useTimelineContext()
  const { onCanvasElementReady: onCanvasElementReadyCallback } =
    useWebWoodContext()
  const scale = useCanvasScale(canvasWrapperEl)

  const visibleMediaIds = usePlayheadOverlappingItems({
    isAnyItemBuffering: isAnyBuffering,
    timelineItems
  })

  const overlappingItemIds = usePlayheadOverlappingItems({
    isAnyItemBuffering: isAnyBuffering,
    timelineItems
  })

  const isActiveMediaVisible =
    activeTimelineItem.id && overlappingItemIds.includes(activeTimelineItem.id)

  const isPending = useSelector(isActiveMediaItemPendingSelector)

  const isMediaControlsVisible =
    isActiveMediaVisible && !isPlaying && !isPending

  useEffect(() => {
    if (existingCanvasElement && containerRef.current) {
      canvasRef.current = existingCanvasElement
      containerRef.current.appendChild(existingCanvasElement)
    }
  }, [existingCanvasElement, containerRef, canvasRef])

  useEffect(() => {
    if (!canvasReady || !canvasRef.current) return
    onCanvasElementReadyCallback(canvasRef.current)
  }, [canvasReady, onCanvasElementReadyCallback])

  const onCanvasElementReady = useCallback(
    (canvasElement: HTMLCanvasElement) => {
      if (!canvasElement) return
      resizeObserverRef.current = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect

        if (!canvasReady && width > 0 && height > 0) {
          setCanvasReady(true)
        }
      })
      canvasRef.current = canvasElement
      resizeObserverRef.current.observe(canvasElement)
    },
    [canvasReady]
  )

  return (
    <>
      {isMediaControlsVisible && <MediaControls />}
      <Styled.ScalableWrapper
        $scale={scale}
        $canvasWidth={canvasWidth}
        $canvasHeight={canvasHeight}
      >
        <Styled.CanvasWrapper ref={containerRef}>
          {canvasOverlayMediaItems.map(mediaItem => (
            <CanvasMediaItem
              key={mediaItem.id}
              mediaItem={mediaItem}
              isVisible={visibleMediaIds.includes(mediaItem.id)}
              scale={scale}
            />
          ))}
          {!existingCanvasElement ? (
            <Styled.Canvas id={CANVAS_ID} ref={onCanvasElementReady} />
          ) : null}
        </Styled.CanvasWrapper>
      </Styled.ScalableWrapper>
    </>
  )
}

export default memo(FilledCanvas)
